// Visual style suggestions computed in the admin's browser with an open-source
// CLIP model (transformers.js). No API key, no subscription: the library is
// loaded from a public CDN on first use and the model (~90 MB) is cached by
// the browser afterwards.

import { TagSuggestion } from "./autoTag";

const TRANSFORMERS_URL =
  "https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.8.1/dist/transformers.min.js";
const MODEL_ID = "Xenova/clip-vit-base-patch32";

// Prompts describing each default style; custom styles get a generic prompt.
const STYLE_PROMPTS: Record<string, string> = {
  "2D": "a hand-drawn 2D animated illustration",
  "3D": "a glossy 3D rendered CGI scene",
  "Flat Design": "a flat design vector illustration with simple shapes",
  "Gradient": "abstract vivid color gradients and glowing shapes",
  "Motion & Video": "live action video footage of real people",
  "Collage": "a paper cut-out collage with photos and textures",
  "Fake 3D": "an isometric pseudo-3D illustration",
};

type Classifier = (
  image: string,
  labels: string[],
  options?: { hypothesis_template?: string },
) => Promise<{ label: string; score: number }[]>;

let classifierPromise: Promise<Classifier> | null = null;

export type VisionStatus = (message: string) => void;

function loadClassifier(onStatus?: VisionStatus): Promise<Classifier> {
  if (!classifierPromise) {
    classifierPromise = (async () => {
      onStatus?.("Chargement du moteur d'analyse…");
      const lib = await import(/* webpackIgnore: true */ TRANSFORMERS_URL);
      lib.env.allowLocalModels = false;
      onStatus?.("Téléchargement du modèle (une seule fois, ~90 Mo)…");
      return lib.pipeline("zero-shot-image-classification", MODEL_ID, {
        dtype: "q8",
        progress_callback: (p: { status: string; progress?: number; file?: string }) => {
          if (p.status === "progress" && typeof p.progress === "number" && /onnx/.test(p.file ?? "")) {
            onStatus?.(`Téléchargement du modèle… ${Math.round(p.progress)} %`);
          }
        },
      }) as Promise<Classifier>;
    })().catch((err) => {
      classifierPromise = null;
      throw err;
    });
  }
  return classifierPromise;
}

/**
 * Scores each style on up to 5 images (thumbnail + styleframes) and returns
 * the styles clearly above the average.
 */
export async function suggestStylesFromImages(
  imageURLs: string[],
  styles: string[],
  onStatus?: VisionStatus,
): Promise<TagSuggestion[]> {
  const images = imageURLs.filter(Boolean).slice(0, 5);
  if (!images.length || !styles.length) return [];

  const classifier = await loadClassifier(onStatus);
  const prompts = styles.map((s) => STYLE_PROMPTS[s] ?? `a motion design frame in ${s} style`);
  const totals = new Map<string, number>();

  let analysed = 0;
  for (let i = 0; i < images.length; i++) {
    onStatus?.(`Analyse de l'image ${i + 1}/${images.length}…`);
    try {
      const output = await classifier(images[i], prompts, { hypothesis_template: "{}" });
      output.forEach(({ label, score }) => totals.set(label, (totals.get(label) ?? 0) + score));
      analysed++;
    } catch (err) {
      // Usually a stored image served without CORS headers (see README, cors.json).
      console.warn("Image ignorée par l'analyse visuelle:", images[i], err);
    }
  }
  if (!analysed) {
    throw new Error(
      "Aucune image n'a pu être lue. Pour les images déjà enregistrées, la configuration CORS du stockage doit être appliquée (voir README).",
    );
  }

  const uniform = 1 / styles.length;
  return styles
    .map((style, idx) => ({ style, score: (totals.get(prompts[idx]) ?? 0) / analysed }))
    .filter(({ score }) => score > uniform * 1.6)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(({ style, score }) => ({
      value: style,
      confidence: Math.min(0.95, score),
      reason: `analyse visuelle (${Math.round(score * 100)} %)`,
    }));
}
